const GRAVITY = 9.81;
const UPWARD_MOVEMENT_EFFICIENCY = 1.3;
const DOWNWARD_MOVEMENT_EFFICIENCY = 0.3;
const JOULES_TO_KCAL = 4184;

const TORSO_HEIGHT_RATIO = 0.30;

const MIN_CONFIDENCE_SCORE = 0.2;
const MAX_CONFIDENCE_SCORE = 0.8;

const KEYPOINT_WEIGHTS = {
  nose: 0.08,
  left_shoulder: 0.0875,
  right_shoulder: 0.0875,
  left_elbow: 0.025,
  right_elbow: 0.025,
  left_wrist: 0.025,
  right_wrist: 0.025,
  left_hip: 0.15,
  right_hip: 0.15,
  left_knee: 0.075,
  right_knee: 0.075,
  left_ankle: 0.075,
  right_ankle: 0.075,
  left_eye: 0.01,
  right_eye: 0.01,
  left_ear: 0.01,
  right_ear: 0.01,
};

/**
 * Calculate confidence weight for a keypoint
 * @param {number} score - Confidence score (0-1)
 * @returns {number} - Weight (0-1)
 */
const getConfidenceWeight = (score) => {
  if (score < MIN_CONFIDENCE_SCORE) return 0;
  if (score > MAX_CONFIDENCE_SCORE) return 1;
  return (score - MIN_CONFIDENCE_SCORE) / (MAX_CONFIDENCE_SCORE - MIN_CONFIDENCE_SCORE);
};

/**
 * Calculate the center of mass from pose keypoints
 * @param {Array} keypoints - Array of pose keypoints
 * @returns {Object|null} - {x, y} coordinates or null if no valid keypoints
 */
export const getCenterOfMass = (keypoints) => {
  let sumX = 0,
    sumY = 0,
    totalWeight = 0;

  keypoints.forEach((kp) => {
    const weight = getConfidenceWeight(kp.score);
    sumX += kp.x * weight;
    sumY += kp.y * weight;
    totalWeight += weight;
  });

  return totalWeight > 0 ? { x: sumX / totalWeight, y: sumY / totalWeight } : null;
};

/**
 * Calculate calories burned based on pose movement
 * @param {Object} currentPose - Current pose data
 * @param {Object} previousPose - Previous pose data
 * @param {number} pixelsPerMeter - Conversion factor from pixels to meters
 * @param {number} weight - User's weight in kg
 * @returns {number} - Calories burned in kcal
 */
export const calculateCalories = (
  currentPose,
  previousPose,
  pixelsPerMeter,
  weight
) => {
  if (!previousPose || !weight) return 0;

  let totalCalories = 0;

  // 1. Center of mass movement (vertical displacement)
  const currentCOM = getCenterOfMass(currentPose.keypoints);
  const previousCOM = getCenterOfMass(previousPose.keypoints);

  if (currentCOM && previousCOM) {
    // Y is inverted in screen coordinates (down is positive)
    const verticalDisplacement =
      (previousCOM.y - currentCOM.y) / pixelsPerMeter; // meters
    const potentialEnergy = weight * GRAVITY * verticalDisplacement; // Joules

    if (verticalDisplacement > 0) {
      // Moving up: body works harder
      totalCalories +=
        (potentialEnergy * UPWARD_MOVEMENT_EFFICIENCY) / JOULES_TO_KCAL;
    } else if (verticalDisplacement < 0) {
      // Moving down: eccentric muscle work absorbs energy
      totalCalories +=
        (Math.abs(potentialEnergy) * DOWNWARD_MOVEMENT_EFFICIENCY) /
        JOULES_TO_KCAL;
    }
  }

  // 2. Individual keypoint movements
  currentPose.keypoints.forEach((currentKp) => {
    const keypointWeight = KEYPOINT_WEIGHTS[currentKp.name];
    if (!keypointWeight) return;

    const previousKp = previousPose.keypoints.find(
      (kp) => kp.name === currentKp.name
    );
    if (!previousKp) return;

    const confidenceWeight = getConfidenceWeight(currentKp.score);
    if (confidenceWeight === 0) return;

    const verticalDisplacement =
      (previousKp.y - currentKp.y) / pixelsPerMeter;
    const keypointMass = weight * keypointWeight;
    const potentialEnergy = keypointMass * GRAVITY * verticalDisplacement;

    if (verticalDisplacement > 0) {
      totalCalories +=
        (potentialEnergy *
          UPWARD_MOVEMENT_EFFICIENCY *
          confidenceWeight) /
        JOULES_TO_KCAL;
    } else if (verticalDisplacement < 0) {
      totalCalories +=
        (Math.abs(potentialEnergy) *
          DOWNWARD_MOVEMENT_EFFICIENCY *
          confidenceWeight) /
        JOULES_TO_KCAL;
    }
  });

  // 3. TODO: Anaerobic respiration bonus
  // Track sustained high-intensity periods and add bonus calories

  return totalCalories;
};

/**
 * Calculate pixels per meter using anatomical proportions
 * @param {number} heightCm - User's actual height in centimeters
 * @param {Array} keypoints - Pose keypoints
 * @returns {number} - Pixels per meter
 */
export const calculatePixelsPerMeter = (heightCm, keypoints) => {
  const leftShoulder = keypoints.find(kp => kp.name === 'left_shoulder');
  const rightShoulder = keypoints.find(kp => kp.name === 'right_shoulder');
  const leftHip = keypoints.find(kp => kp.name === 'left_hip');
  const rightHip = keypoints.find(kp => kp.name === 'right_hip');

  if (!leftShoulder || !rightShoulder || !leftHip || !rightHip) {
    return 0;
  }

  const shoulderY = (leftShoulder.y + rightShoulder.y) / 2;
  const hipY = (leftHip.y + rightHip.y) / 2;

  const torsoHeightInPixels = Math.abs(hipY - shoulderY);

  const torsoHeightInMeters = (heightCm * TORSO_HEIGHT_RATIO) / 100;

  return torsoHeightInPixels / torsoHeightInMeters;
};
